/**
 * Bundle 139 patch - Admin integration secrets page.
 *
 * Manages user-editable integration secrets that used to live in
 * env vars. On Emergent-hosted prod there is no user-editable env,
 * so this page is the ONLY way to configure SENTRY_DSN, PagerDuty
 * routing key, PostHog, Resend, Stripe API key, etc.
 *
 * Reads are always masked. Only the update modal ever collects raw
 * plaintext (password input, autocomplete off, spellcheck off).
 *
 * Note: distinct from `AdminIntegrationsPage` (which is the read-only
 * integrations HEALTH dashboard). This page manages the SECRETS.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Lock, ShieldAlert, Wallet } from 'lucide-react';

const SOURCE_TONE = {
  db: 'bg-secondary-100 text-secondary-800 border-secondary-200',
  env: 'bg-amber-100 text-amber-800 border-amber-200',
  not_configured: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

const AdminIntegrationSecretsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings/integrations');
      setRows(data?.secrets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load integrations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-integration-secrets-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1">
            Settings * Bundle 139
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900 flex items-center gap-2">
            <Lock className="h-6 w-6" /> Integration secrets
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-2xl">
            Manage user-facing integration keys. On Emergent-hosted prod there
            is no user-editable env, so this page is the ONLY way to set these.
            Values are stored encrypted and never returned in plaintext by any
            GET.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="integration-secrets-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3">
        {rows.map((s) => (
          <Card key={s.key} data-testid={`integration-secret-row-${s.key}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-primary-900">{s.key}</span>
                    <span
                      data-testid={`integration-secret-source-${s.key}`}
                      className={`text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 border ${SOURCE_TONE[s.source] || SOURCE_TONE.not_configured}`}
                    >
                      {s.source.replace('_', ' ')}
                    </span>
                    {s.wallet_risk ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 bg-accent-100 text-accent-800 border border-accent-200">
                        <Wallet className="h-3 w-3" /> wallet risk
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-primary-700 mt-1">{s.description}</p>
                  <div className="text-xs text-neutral-500 mt-2 font-mono" data-testid={`integration-secret-masked-${s.key}`}>
                    {s.masked_value || 'not configured'}
                  </div>
                  {s.updated_at ? (
                    <div className="text-[11px] text-neutral-500 mt-1">
                      updated by {s.updated_by_admin_id || 'unknown'} at {new Date(s.updated_at).toLocaleString()}
                    </div>
                  ) : null}
                </div>
                <Button size="sm" onClick={() => setEditing(s)}
                            data-testid={`integration-secret-edit-btn-${s.key}`}
                            className="bg-primary-800 hover:bg-primary-900 text-white">
                  Update
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && !loading ? (
          <Card>
            <CardContent className="p-6 text-neutral-500 text-sm" data-testid="integration-secrets-empty">
              No integration secrets registered.
            </CardContent>
          </Card>
        ) : null}
      </div>

      <EditSecretDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
};

const EditSecretDialog = ({ row, onClose, onSaved }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setValue(''); }, [row]);
  if (!row) return null;

  const save = async () => {
    if (!value.trim()) { toast.error('Value cannot be empty.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/settings/integrations/${row.key}`,
        { value: value.trim() },
      );
      if (data?.ok) {
        toast.success(`${row.key} updated (${data.masked_value}).`);
        onSaved && onSaved();
      } else {
        toast.error('Update failed.');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : d?.message;
      toast.error(msg || 'Update failed.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`integration-secret-dialog-${row.key}`}>
        <DialogHeader>
          <DialogTitle>Update {row.key}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-primary-800">{row.description}</p>
        <div className="text-[11px] text-neutral-500 font-mono">
          Example format: {row.example}
        </div>
        {row.wallet_risk ? (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              This key can trigger real-money movement. Double check the value
              before saving.
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-1">
          <Label htmlFor="secret-value">New value</Label>
          <Input
            id="secret-value"
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid={`integration-secret-input-${row.key}`}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
                      data-testid={`integration-secret-cancel-${row.key}`}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || !value.trim()}
            className="bg-primary-800 hover:bg-primary-900 text-white"
            data-testid={`integration-secret-save-${row.key}`}
          >
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminIntegrationSecretsPage;
